import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { StudentReferralAppliedDiscount, StudentReferralDiscountResponse } from '@edtech-ui/core/models/student-courses-v2/student-referral-code';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-referral-code-dialog-box',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, HasPermissionDirective],
	templateUrl: './referral-code-dialog-box.component.html',
	styleUrl: './referral-code-dialog-box.component.scss'
})
export class ReferralCodeDialogBoxComponent implements OnDestroy {

	referralCodeForm: FormGroup;
	isCodeValid: boolean | null = null;
	subscription = new Subscription();
	referralData: StudentReferralAppliedDiscount[] = [];
	pricingId: string;
	isApplying = false;
	discountedPrice: number = 0;
	referralCode = '';
	PermissionForStudents = PermissionForStudents;
	planName: string;
	price: number;
	private referralCheckSubscription: Subscription | null = null;

	constructor(
		private dialogRef: MatDialogRef<ReferralCodeDialogBoxComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { pricing_id: string, plan_name: string, price: number },
		private _paymentService: PaymentService,
		private _toastr: ToastrService,
	) {
		this.pricingId = data.pricing_id;
		this.planName = data.plan_name;
		this.price = data.price;

		this.referralCodeForm = new FormGroup({
			referral_code: new FormControl('')
		});
	}

	close() {
		this.dialogRef.close();
	}

	postReferralCode() {
		if (this.isApplying) return;

		const referralCode = this.referralCodeForm.get('referral_code')?.value?.trim();

		if (!referralCode) {
			this.isCodeValid = false;
			return;
		}

		if (!this.pricingId) {
			this._toastr.error('Invalid pricing ID');
			return;
		}

		this.isApplying = true;
		this.isCodeValid = null;
		this.referralData = [];

		if (this.referralCheckSubscription) {
			this.referralCheckSubscription.unsubscribe();
		}

		this.referralCheckSubscription = this._paymentService.getStudentReferralCode(this.pricingId, referralCode, false).pipe(
			tap((response: StudentReferralDiscountResponse) => {
				if (response.Success && response.Data) {
					this.referralData = response.Data.applied_discounts || [];
					this.isCodeValid = response.Data.valid ?? (this.referralData.length > 0);
					this.discountedPrice = response.Data.discounted_price;
				} else {
					this.isCodeValid = false;
				}
			}),
			finalize(() => {
				this.isApplying = false
			}),
			catchError(() => {
				this.isCodeValid = false;
				this._toastr.error('Something went wrong while sending referral code. Please try again later');
				return of(null);
			}),
		).subscribe();

		this.subscription.add(this.referralCheckSubscription);
	}

	payNow() {
		if (this.isCodeValid && this.referralData.length) {
			const referralCode = this.referralCodeForm.get('referral_code')?.value;
			this.dialogRef.close({
				isReferralApplied: true,
				discountedPrice: this.discountedPrice,
				referralCode,
				use_usage_count: false
			});
		} else {
			this.dialogRef.close({
				isReferralApplied: false
			})
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}